// frontend/src/components/maps/RoutePlannerMap.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import maplibregl from "maplibre-gl";
import { useTranslation } from "react-i18next";

import axios from "../../axiosConfig";

import "maplibre-gl/dist/maplibre-gl.css";
import "../../styles/MapBase.css";
import "../../styles/RoutePlannerMap.css";

const MAP_STYLE = "https://tiles.openfreemap.org/styles/bright";

const DEFAULT_CENTER = [-8.61099, 41.14961];
const DEFAULT_ZOOM = 5;

const ROUTE_SOURCE_ID = "route-template-preview-route";
const ROUTE_CASING_LAYER_ID = "route-template-preview-route-casing";
const ROUTE_LINE_LAYER_ID = "route-template-preview-route-line";

const POINTS_SOURCE_ID = "route-template-preview-points";
const POINTS_HALO_LAYER_ID = "route-template-preview-points-halo";
const POINTS_CIRCLE_LAYER_ID = "route-template-preview-points-circle";
const POINTS_LABEL_LAYER_ID = "route-template-preview-points-label";

function normalizeCenter(center) {
  if (
    Array.isArray(center) &&
    center.length >= 2 &&
    Number.isFinite(Number(center[0])) &&
    Number.isFinite(Number(center[1]))
  ) {
    return [Number(center[0]), Number(center[1])];
  }

  return DEFAULT_CENTER;
}

function normalizeZoom(zoom) {
  const parsedZoom = Number(zoom);

  return Number.isFinite(parsedZoom)
    ? parsedZoom
    : DEFAULT_ZOOM;
}

function normalizePoint(point) {
  if (!point) return null;

  const lat = Number(point.lat);
  const lng = Number(point.lng);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }

  return {
    ...point,
    lat,
    lng,
    type: String(point.type || "VIA").toUpperCase(),
  };
}

function orderRoutePoints(points) {
  const normalizedPoints = (Array.isArray(points) ? points : [])
    .map(normalizePoint)
    .filter(Boolean);

  const origin = normalizedPoints.find(
    (point) => point.type === "ORIGIN"
  );

  const destination = normalizedPoints.find(
    (point) => point.type === "DESTINATION"
  );

  const intermediatePoints =
    normalizedPoints
      .filter(
        (point) =>
          point.type !== "ORIGIN" &&
          point.type !==
            "DESTINATION"
      )
      .sort((a, b) => {
        return (
          (Number(a.sequence) || 0) -
          (Number(b.sequence) || 0)
        );
      });

  return [
    origin,
    ...intermediatePoints,
    destination,
  ]
    .filter(Boolean)
    .map((point, index) => ({
      ...point,
      sequence: index + 1,
    }));
}

function normalizeRouteResponse(
  data
) {
  const routes =
    Array.isArray(
      data?.routes
    )
      ? data.routes
      : [];

  const route =
    routes[0];

  if (!route) {
    return null;
  }

  const geometry =
    route.geometry?.type ===
    "Feature"
      ? route.geometry
      : route.geometry?.type ===
          "LineString"
        ? {
            type:
              "Feature",

            geometry:
              route.geometry,

            properties:
              {},
          }
        : null;

  if (
    geometry?.geometry?.type !==
      "LineString" ||
    !Array.isArray(
      geometry.geometry.coordinates
    ) ||
    geometry.geometry.coordinates.length <
      2
  ) {
    return null;
  }

  return {
    id: route.id || "route-template-preview",
    distanceKm:
      route.distanceKm ??
      route.distance_km ??
      null,
    durationSeconds:
      route.durationSeconds ??
      route.duration_seconds ??
      null,
    hasTolls:
      route.hasTolls ??
      route.has_tolls ??
      route.hasToll ??
      route.has_toll ??
      route.summary?.hasTolls ??
      route.summary?.has_tolls ??
      false,
    tollEstimate:
      route.tollEstimate ??
      route.toll_estimate ??
      route.summary?.tollEstimate ??
      route.summary?.toll_estimate ??
      null,
    source:
      route.source ||
      data?.provider ||
      "VALHALLA",
    geometry,
  };
}

function removeLayerIfExists(map, layerId) {
  try {
    if (map?.getLayer?.(layerId)) {
      map.removeLayer(layerId);
    }
  } catch {}
}

function removeSourceIfExists(map, sourceId) {
  try {
    if (map?.getSource?.(sourceId)) {
      map.removeSource(sourceId);
    }
  } catch {}
}

function clearPlannerLayers(map) {
  [
    POINTS_LABEL_LAYER_ID,
    POINTS_CIRCLE_LAYER_ID,
    POINTS_HALO_LAYER_ID,
    ROUTE_LINE_LAYER_ID,
    ROUTE_CASING_LAYER_ID,
  ].forEach((layerId) => {
    removeLayerIfExists(map, layerId);
  });

  [
    POINTS_SOURCE_ID,
    ROUTE_SOURCE_ID,
  ].forEach((sourceId) => {
    removeSourceIfExists(map, sourceId);
  });
}

function getPointLabel(point, viaIndex) {
  if (point.type === "ORIGIN") return "A";
  if (point.type === "DESTINATION") return "B";

  return String(viaIndex + 1);
}

function buildPointsFeatureCollection(points) {
  let viaIndex = 0;

  return {
    type: "FeatureCollection",
    features: points.map((point) => {
      const currentViaIndex = viaIndex;

      if (point.type === "VIA") {
        viaIndex += 1;
      }

      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [
            point.lng,
            point.lat,
          ],
        },
        properties: {
          id: String(point.id || ""),
          pointType: point.type,
          label: getPointLabel(
            point,
            currentViaIndex
          ),
        },
      };
    }),
  };
}

function normalizeStoredRoute(route) {
  if (!route || !route.geometry) {
    return null;
  }

  return {
    ...route,
    source:
      route.source ||
      route.routeSource ||
      "SAVED_ROUTE",
  };
}

function getPickHintKey(pickingType) {
  if (pickingType === "ORIGIN") {
    return "map.planner.pickHint.origin";
  }

  if (pickingType === "DESTINATION") {
    return "map.planner.pickHint.destination";
  }

  return "map.planner.pickHint.via";
}

export default function RoutePlannerMap({
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  ariaLabel,
  onReady,
  points = [],
  pickingType = null,
  onPointPicked,
  onPointMoved,
  avoidTolls = false,
  onRouteChange,
  routeOverride = null,
  calculateRoute = true,
}) {
  const { t, i18n } = useTranslation("map");

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  const onReadyRef = useRef(onReady);
  const onRouteChangeRef = useRef(onRouteChange);
  const onPointPickedRef = useRef(onPointPicked);
  const onPointMovedRef = useRef(onPointMoved);

  const reverseGeocodingRef = useRef(false);
  const dragStateRef = useRef(null);

  const initialCenterRef = useRef(
    normalizeCenter(center)
  );

  const initialZoomRef = useRef(
    normalizeZoom(zoom)
  );

  const lastFitSignatureRef = useRef("");

  const [retryKey, setRetryKey] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const [isMapVisuallyReady, setIsMapVisuallyReady] = useState(false);
  const [hasMapError, setHasMapError] = useState(false);
  const [routeResult, setRouteResult] = useState(null);

  const [
    isReverseGeocoding,
    setIsReverseGeocoding,
  ] = useState(false);

  const [
    reverseGeocodeError,
    setReverseGeocodeError,
  ] = useState(null);

  const [
    isDraggingPoint,
    setIsDraggingPoint,
  ] = useState(false);

  const orderedPoints = useMemo(() => {
    return orderRoutePoints(points);
  }, [points]);

  const pointsSignature = useMemo(() => {
    return orderedPoints
      .map((point) => {
        return [
          point.id,
          point.type,
          point.lat,
          point.lng,
        ].join(":");
      })
      .join("|");
  }, [orderedPoints]);

  const hasOrigin = orderedPoints.some(
    (point) => point.type === "ORIGIN"
  );

  const hasDestination = orderedPoints.some(
    (point) => point.type === "DESTINATION"
  );

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    onRouteChangeRef.current = onRouteChange;
  }, [onRouteChange]);

  useEffect(() => {
    onPointPickedRef.current = onPointPicked;
  }, [onPointPicked]);

  useEffect(() => {
    onPointMovedRef.current = onPointMoved;
  }, [onPointMoved]);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return undefined;
    }

    let map = null;
    let isDestroyed = false;
    let hasLoaded = false;
    let readyFallbackTimer = null;
    let resizeAnimationFrame = null;
    let resizeObserver = null;

    setMapReady(false);
    setIsMapVisuallyReady(false);
    setHasMapError(false);
    lastFitSignatureRef.current = "";

    const handleResize = () => {
      if (
        isDestroyed ||
        !map ||
        mapRef.current !== map
      ) {
        return;
      }

      if (resizeAnimationFrame) {
        window.cancelAnimationFrame(
          resizeAnimationFrame
        );
      }

      resizeAnimationFrame =
        window.requestAnimationFrame(() => {
          if (
            isDestroyed ||
            !map ||
            mapRef.current !== map
          ) {
            return;
          }

          try {
            map.resize();
          } catch {}
        });
    };

    try {
      map = new maplibregl.Map({
        container: mapContainerRef.current,
        style: MAP_STYLE,
        center: initialCenterRef.current,
        zoom: initialZoomRef.current,
        attributionControl: true,
      });

      mapRef.current = map;

      map.addControl(
        new maplibregl.NavigationControl(),
        "top-right"
      );

      const handleMapIdle = () => {
        if (
          isDestroyed ||
          mapRef.current !== map
        ) {
          return;
        }

        if (readyFallbackTimer) {
          window.clearTimeout(
            readyFallbackTimer
          );

          readyFallbackTimer = null;
        }

        setIsMapVisuallyReady(true);
      };

      const handleMapLoad = () => {
        if (
          isDestroyed ||
          mapRef.current !== map
        ) {
          return;
        }

        hasLoaded = true;

        setMapReady(true);
        setHasMapError(false);

        handleResize();

        readyFallbackTimer =
          window.setTimeout(() => {
            if (
              isDestroyed ||
              mapRef.current !== map
            ) {
              return;
            }

            setIsMapVisuallyReady(true);
          }, 900);

        onReadyRef.current?.(map);
      };

      const handleMapError = (event) => {
        if (
          hasLoaded ||
          isDestroyed
        ) {
          return;
        }

        console.error(
          "[RoutePlannerMap] Failed to load map:",
          event?.error?.message ||
            event?.error ||
            event
        );

        setHasMapError(true);
        setIsMapVisuallyReady(false);
      };

      map.on("load", handleMapLoad);
      map.once("idle", handleMapIdle);
      map.on("error", handleMapError);

      if (
        typeof ResizeObserver !== "undefined"
      ) {
        resizeObserver = new ResizeObserver(
          handleResize
        );

        resizeObserver.observe(
          mapContainerRef.current
        );
      }

      window.addEventListener(
        "resize",
        handleResize
      );

      return () => {
        isDestroyed = true;

        if (readyFallbackTimer) {
          window.clearTimeout(
            readyFallbackTimer
          );
        }

        if (resizeAnimationFrame) {
          window.cancelAnimationFrame(
            resizeAnimationFrame
          );
        }

        resizeObserver?.disconnect();

        window.removeEventListener(
          "resize",
          handleResize
        );

        if (mapRef.current === map) {
          mapRef.current = null;
        }

        try {
          clearPlannerLayers(map);

          map.off("load", handleMapLoad);
          map.off("idle", handleMapIdle);
          map.off("error", handleMapError);
          map.remove();
        } catch (error) {
          console.warn(
            "[RoutePlannerMap] Ignored map cleanup error:",
            error?.message
          );
        }
      };
    } catch (error) {
      console.error(
        "[RoutePlannerMap] Failed to create map:",
        error
      );

      setHasMapError(true);
      setIsMapVisuallyReady(false);

      return undefined;
    }
  }, [retryKey]);

  /*
   * Seleção de origem, destino ou ponto de passagem
   * diretamente através de clique no mapa.
   *
   * Depois do clique, identificamos o local através
   * do backend. Caso o geocoding falhe, mantemos as
   * coordenadas para não impedir a criação da rota.
   */
  useEffect(() => {
    if (
      !mapReady ||
      !mapRef.current
    ) {
      return undefined;
    }

    const map = mapRef.current;

    let cancelled = false;

    map.getCanvas().style.cursor =
      pickingType
        ? "crosshair"
        : "";

    if (pickingType) {
      setReverseGeocodeError(null);
    }

    const handleMapClick =
      async (event) => {
        if (
          !pickingType ||
          reverseGeocodingRef.current
        ) {
          return;
        }

        const clickedPoint = {
          type: pickingType,
          lat: event.lngLat.lat,
          lng: event.lngLat.lng,
        };

        reverseGeocodingRef.current =
          true;

        setIsReverseGeocoding(true);
        setReverseGeocodeError(null);

        map.getCanvas().style.cursor =
          "wait";

        try {
          const { data } =
            await axios.get(
              "/maps/reverse-geocode",
              {
                params: {
                  lat:
                    clickedPoint.lat,
                  lng:
                    clickedPoint.lng,
                  language:
                    i18n.resolvedLanguage ||
                    i18n.language ||
                    "pt-PT",
                },
              }
            );

          if (cancelled) return;

          const item =
            data?.item || null;

          onPointPickedRef.current?.({
            ...clickedPoint,

            lat: Number(
              item?.lat ??
              clickedPoint.lat
            ),

            lng: Number(
              item?.lng ??
              clickedPoint.lng
            ),

            name:
              item?.name || null,

            address:
              item?.address || null,

            postalCode:
              item?.postalCode || null,

            city:
              item?.city || null,

            country:
              item?.country || null,

            countryCode:
              item?.countryCode ||
              null,
          });
        } catch (error) {
          if (cancelled) return;

          console.warn(
            "[RoutePlannerMap] Reverse geocoding failed:",
            error?.message
          );

          setReverseGeocodeError(
            t(
              "map.planner.errors.reverseGeocoding",
              {
                defaultValue:
                  "Não foi possível identificar a morada. O ponto foi guardado pelas coordenadas.",
              }
            )
          );

          onPointPickedRef.current?.(
            clickedPoint
          );
        } finally {
          if (!cancelled) {
            reverseGeocodingRef.current =
              false;

            setIsReverseGeocoding(false);

            map.getCanvas().style.cursor =
              pickingType
                ? "crosshair"
                : "";
          }
        }
      };

    map.on("click", handleMapClick);

    return () => {
      cancelled = true;

      reverseGeocodingRef.current =
        false;

      try {
        map.off(
          "click",
          handleMapClick
        );

        if (mapRef.current === map) {
          map.getCanvas().style.cursor =
            "";
        }
      } catch {}
    };
  }, [
    i18n.language,
    i18n.resolvedLanguage,
    mapReady,
    pickingType,
    t,
  ]);

  /*
   * Arrastar pontos existentes diretamente no mapa.
   *
   * Durante o movimento atualizamos apenas o marcador.
   * O trajeto e a morada são atualizados depois de
   * o utilizador largar o ponto.
   */
  useEffect(() => {
    if (
      !mapReady ||
      !mapRef.current ||
      !calculateRoute
    ) {
      return undefined;
    }

    const map = mapRef.current;

    let cancelled = false;

    const getPointFeature = (event) => {
      if (
        pickingType ||
        !event?.point
      ) {
        return null;
      }

      const layers = [
        POINTS_CIRCLE_LAYER_ID,
        POINTS_HALO_LAYER_ID,
      ].filter((layerId) => {
        return Boolean(
          map.getLayer(layerId)
        );
      });

      if (!layers.length) {
        return null;
      }

      try {
        const features =
          map.queryRenderedFeatures(
            event.point,
            {
              layers,
            }
          );

        return (
          features.find(
            (feature) =>
              feature?.properties?.id
          ) || null
        );
      } catch {
        return null;
      }
    };

    const restoreMapInteraction = () => {
      try {
        if (!map.dragPan.isEnabled()) {
          map.dragPan.enable();
        }

        map.getCanvas().style.cursor =
          pickingType
            ? "crosshair"
            : "";
      } catch {}
    };

    const handleMouseDown = (event) => {
      if (
        pickingType ||
        reverseGeocodingRef.current
      ) {
        return;
      }

      if (
        event?.originalEvent?.button !==
          undefined &&
        event.originalEvent.button !== 0
      ) {
        return;
      }

      const feature =
        getPointFeature(event);

      const pointId =
        feature?.properties?.id;

      if (!pointId) {
        return;
      }

      const point =
        orderedPoints.find(
          (candidate) =>
            String(candidate.id) ===
            String(pointId)
        );

      if (!point) {
        return;
      }

      event.preventDefault?.();

      dragStateRef.current = {
        point,
        pointId:
          String(point.id),
        lat: point.lat,
        lng: point.lng,
        moved: false,
      };

      setIsDraggingPoint(true);
      setReverseGeocodeError(null);

      try {
        map.dragPan.disable();
        map.getCanvas().style.cursor =
          "grabbing";
      } catch {}
    };

    const handleMouseMove = (event) => {
      const dragState =
        dragStateRef.current;

      if (!dragState) {
        if (!pickingType) {
          const feature =
            getPointFeature(event);

          map.getCanvas().style.cursor =
            feature
              ? "grab"
              : "";
        }

        return;
      }

      if (!event?.lngLat) {
        return;
      }

      dragState.lat =
        event.lngLat.lat;

      dragState.lng =
        event.lngLat.lng;

      dragState.moved = true;

      const source =
        map.getSource(
          POINTS_SOURCE_ID
        );

      if (
        source &&
        typeof source.setData ===
          "function"
      ) {
        const previewPoints =
          orderedPoints.map(
            (point) => {
              if (
                String(point.id) !==
                dragState.pointId
              ) {
                return point;
              }

              return {
                ...point,
                lat:
                  dragState.lat,
                lng:
                  dragState.lng,
              };
            }
          );

        source.setData(
          buildPointsFeatureCollection(
            previewPoints
          )
        );
      }

      event.preventDefault?.();
    };

    const finishDragging =
      async (event = null) => {
        const dragState =
          dragStateRef.current;

        if (!dragState) {
          return;
        }

        if (event?.lngLat) {
          dragState.lat =
            event.lngLat.lat;

          dragState.lng =
            event.lngLat.lng;
        }

        dragStateRef.current = null;

        setIsDraggingPoint(false);

        restoreMapInteraction();

        if (
          !dragState.moved ||
          !onPointMovedRef.current
        ) {
          return;
        }

        const movedPoint = {
          ...dragState.point,
          lat:
            dragState.lat,
          lng:
            dragState.lng,
        };

        reverseGeocodingRef.current =
          true;

        setIsReverseGeocoding(true);
        setReverseGeocodeError(null);

        try {
          const { data } =
            await axios.get(
              "/maps/reverse-geocode",
              {
                params: {
                  lat:
                    movedPoint.lat,
                  lng:
                    movedPoint.lng,
                  language:
                    i18n.resolvedLanguage ||
                    i18n.language ||
                    "pt-PT",
                },
              }
            );

          if (cancelled) {
            return;
          }

          const item =
            data?.item || null;

          onPointMovedRef.current?.({
            ...movedPoint,

            lat: Number(
              item?.lat ??
              movedPoint.lat
            ),

            lng: Number(
              item?.lng ??
              movedPoint.lng
            ),

            name:
              item?.name || null,

            address:
              item?.address || null,

            postalCode:
              item?.postalCode || null,

            city:
              item?.city || null,

            country:
              item?.country || null,

            countryCode:
              item?.countryCode ||
              null,
          });
        } catch (error) {
          if (cancelled) {
            return;
          }

          console.warn(
            "[RoutePlannerMap] Reverse geocoding after drag failed:",
            error?.message
          );

          setReverseGeocodeError(
            t(
              "map.planner.errors.reverseGeocoding",
              {
                defaultValue:
                  "Não foi possível identificar a morada. O ponto foi guardado pelas coordenadas.",
              }
            )
          );

          onPointMovedRef.current?.({
            ...movedPoint,
            name: null,
            address: null,
            postalCode: null,
            city: null,
            country: null,
            countryCode: null,
          });
        } finally {
          if (!cancelled) {
            reverseGeocodingRef.current =
              false;

            setIsReverseGeocoding(false);
          }
        }
      };

    const handleMouseUp = (event) => {
      finishDragging(event);
    };

    const handleWindowMouseUp = () => {
      finishDragging();
    };

    map.on(
      "mousedown",
      handleMouseDown
    );

    map.on(
      "mousemove",
      handleMouseMove
    );

    map.on(
      "mouseup",
      handleMouseUp
    );

    window.addEventListener(
      "mouseup",
      handleWindowMouseUp
    );

    return () => {
      cancelled = true;

      dragStateRef.current = null;

      try {
        map.off(
          "mousedown",
          handleMouseDown
        );

        map.off(
          "mousemove",
          handleMouseMove
        );

        map.off(
          "mouseup",
          handleMouseUp
        );
      } catch {}

      window.removeEventListener(
        "mouseup",
        handleWindowMouseUp
      );

      restoreMapInteraction();
    };
  }, [
    calculateRoute,
    i18n.language,
    i18n.resolvedLanguage,
    mapReady,
    orderedPoints,
    pickingType,
    t,
  ]);

  /*
   * Calcular trajeto sempre que os pontos ou
   * a opção de portagens forem alterados.
   */
  useEffect(() => {
    const storedRoute =
      !calculateRoute
        ? normalizeStoredRoute(
            routeOverride
          )
        : null;

    if (!calculateRoute) {
      const readOnlyRoute =
        storedRoute ||
        null;

      setRouteResult(
        readOnlyRoute
      );

      onRouteChangeRef.current?.({
        route:
          readOnlyRoute,

        loading:
          false,

        error:
          storedRoute ||
          !routeOverride
            ? null
            : t(
                "map.planner.errors.savedRouteGeometry",
                {
                  defaultValue:
                    "A rota guardada não tem uma geometria válida.",
                }
              ),
      });

      return undefined;
    }

    if (
      !hasOrigin ||
      !hasDestination ||
      orderedPoints.length < 2
    ) {
      setRouteResult(null);

      onRouteChangeRef.current?.({
        route: null,
        loading: false,
        error: null,
      });

      return undefined;
    }

    let cancelled = false;

    async function calculateRouteFromPoints() {
      setRouteResult(null);

      onRouteChangeRef.current?.({
        route: null,
        loading: true,
        error: null,
      });

      try {
        const response = await axios.post(
          "/maps/route",
          {
            costing: "truck",
            alternatives: 0,
            avoidTolls,
            includeTollEstimate: true,
            tollVehicleClass: 4,
            locations: orderedPoints.map(
              (point, index) => ({
                id:
                  point.id ||
                  `route-point-${index}`,
                type: point.type,
                sequence: index + 1,
                lat: point.lat,
                lon: point.lng,
              })
            ),
          }
        );

        if (cancelled) return;

        const normalizedRoute =
          normalizeRouteResponse(
            response.data
          );

        if (!normalizedRoute) {
          throw new Error(
            "O motor de rotas não devolveu uma geometria válida."
          );
        }

        setRouteResult(
          normalizedRoute
        );

        onRouteChangeRef.current?.({
          route:
            normalizedRoute,

          loading:
            false,

          error:
            null,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.warn(
          "[RoutePlannerMap] Real routing unavailable:",
          error?.message
        );

        setRouteResult(
          null
        );

        onRouteChangeRef.current?.({
          route:
            null,

          loading:
            false,

          error:
            t(
              "map.planner.errors.routing",
              {
                defaultValue:
                  "Não foi possível calcular a rota por estrada.",
              }
            ),
        });
      }
    }

    calculateRouteFromPoints();

    return () => {
      cancelled = true;
    };
  }, [
    avoidTolls,
    calculateRoute,
    hasDestination,
    hasOrigin,
    orderedPoints,
    routeOverride,
    t,
  ]);

  /*
   * Desenhar pontos e trajeto sem recriar o mapa.
   */
  useEffect(() => {
    if (
      !mapReady ||
      !mapRef.current
    ) {
      return;
    }

    const map = mapRef.current;

    clearPlannerLayers(map);

    if (routeResult?.geometry) {
      map.addSource(ROUTE_SOURCE_ID, {
        type: "geojson",
        data: routeResult.geometry,
      });

      map.addLayer({
        id: ROUTE_CASING_LAYER_ID,
        type: "line",
        source: ROUTE_SOURCE_ID,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#ffffff",
          "line-width": 9,
          "line-opacity": 0.96,
        },
      });

      map.addLayer({
        id: ROUTE_LINE_LAYER_ID,
        type: "line",
        source: ROUTE_SOURCE_ID,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color":
            "#3214d6",

          "line-width":
            5,

          "line-opacity":
            0.98,
        },
      });
    }

    if (orderedPoints.length > 0) {
      map.addSource(POINTS_SOURCE_ID, {
        type: "geojson",
        data: buildPointsFeatureCollection(
          orderedPoints
        ),
      });

      map.addLayer({
        id: POINTS_HALO_LAYER_ID,
        type: "circle",
        source: POINTS_SOURCE_ID,
        paint: {
          "circle-radius": 17,
          "circle-color": "#ffffff",
          "circle-opacity": 0.97,
          "circle-stroke-color":
            "rgba(15, 23, 42, 0.16)",
          "circle-stroke-width": 1,
        },
      });

      map.addLayer({
        id: POINTS_CIRCLE_LAYER_ID,
        type: "circle",
        source: POINTS_SOURCE_ID,
        paint: {
          "circle-radius": 13,
          "circle-color": [
            "match",
            ["get", "pointType"],
            "ORIGIN",
            "#0b8dbd",
            "DESTINATION",
            "#111827",
            "VIA",
            "#9333ea",
            "#64748b",
          ],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
          "circle-opacity": 0.98,
        },
      });

      map.addLayer({
        id: POINTS_LABEL_LAYER_ID,
        type: "symbol",
        source: POINTS_SOURCE_ID,
        layout: {
          "text-field": ["get", "label"],
          "text-size": 11,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color":
            "rgba(15, 23, 42, 0.08)",
          "text-halo-width": 0.4,
        },
      });
    }

    if (
      !pointsSignature ||
      lastFitSignatureRef.current ===
        pointsSignature
    ) {
      return;
    }

    const bounds =
      new maplibregl.LngLatBounds();

    orderedPoints.forEach((point) => {
      bounds.extend([
        point.lng,
        point.lat,
      ]);
    });

    if (
      routeResult?.geometry?.geometry
        ?.type === "LineString"
    ) {
      routeResult.geometry.geometry.coordinates.forEach(
        (coordinate) => {
          if (
            Array.isArray(coordinate) &&
            coordinate.length >= 2
          ) {
            bounds.extend(coordinate);
          }
        }
      );
    }

    if (orderedPoints.length >= 2) {
      map.fitBounds(bounds, {
        padding: {
          top: 90,
          bottom: 70,
          left: 80,
          right: 80,
        },
        maxZoom: 13,
        duration: 650,
      });
    } else if (orderedPoints.length === 1) {
      map.flyTo({
        center: [
          orderedPoints[0].lng,
          orderedPoints[0].lat,
        ],
        zoom: 12,
        duration: 600,
      });
    }

    lastFitSignatureRef.current =
      pointsSignature;
  }, [
    mapReady,
    orderedPoints,
    pointsSignature,
    routeResult,
  ]);

  const handleRetry = () => {
    setRetryKey(
      (current) => current + 1
    );
  };

  return (
    <div
      className={[
        "map-base",
        "route-planner-map",
        isMapVisuallyReady
          ? "is-map-ready"
          : "is-map-loading",
        hasMapError
          ? "has-map-error"
          : "",
        pickingType
          ? "is-picking-point"
          : "",
        isDraggingPoint
          ? "is-dragging-point"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={
        ariaLabel ||
        t("map.planner.ariaLabel", {
          defaultValue:
            "Planeador de rotas",
        })
      }
    >
      <div
        ref={mapContainerRef}
        className="map-base__canvas route-planner-map__canvas"
      />

    {(pickingType ||
      isDraggingPoint ||
      isReverseGeocoding) &&
      isMapVisuallyReady && (
        <div className="route-planner-map__pick-hint">
          {isReverseGeocoding ? (
            <span className="route-planner-map__pick-spinner" />
          ) : (
            <span />
          )}

          <strong>
            {isReverseGeocoding
              ? t(
                  "map.planner.identifying",
                  {
                    defaultValue:
                      "A identificar o local...",
                  }
                )
              : isDraggingPoint
                ? t(
                    "map.planner.draggingPoint",
                    {
                      defaultValue:
                        "Arraste o ponto para a nova posição.",
                    }
                  )
                : t(
                    getPickHintKey(
                      pickingType
                    ),
                    {
                      defaultValue:
                        "Clique no mapa para selecionar o ponto.",
                    }
                  )}
          </strong>
        </div>
      )}

      {reverseGeocodeError &&
        isMapVisuallyReady && (
          <div className="route-planner-map__geocode-error">
            {reverseGeocodeError}
          </div>
        )}

      {!isMapVisuallyReady &&
        !hasMapError && (
          <div className="route-planner-map__loading-cover">
            <div className="route-planner-map__loading-card">
              <span className="route-planner-map__spinner" />

              <strong>
                {t(
                  "map.planner.loading",
                  {
                    defaultValue:
                      "A carregar mapa...",
                  }
                )}
              </strong>
            </div>
          </div>
        )}

      {hasMapError && (
        <div className="route-planner-map__error-cover">
          <div className="route-planner-map__error-card">
            <strong>
              {t(
                "map.planner.errors.load",
                {
                  defaultValue:
                    "Não foi possível carregar o mapa.",
                }
              )}
            </strong>

            <span>
              {t(
                "map.planner.errors.loadDescription",
                {
                  defaultValue:
                    "Verifique a ligação e tente novamente.",
                }
              )}
            </span>

            <button
              type="button"
              onClick={handleRetry}
            >
              {t(
                "map.planner.errors.retry",
                {
                  defaultValue:
                    "Tentar novamente",
                }
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}